import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Box,
    Typography,
    Button,
    Chip,
    Divider,
    Avatar,
    IconButton,
    Grid,
    Tab,
    Tabs,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tooltip,
    DialogContentText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";

// Componentes
import TicketComments from './TicketComments';
import CreateTicketModal from './CreateTicketModal';

// Acciones
import { getTicketComments, addTicketCommentWithUserName } from '../../../reducers/kanban/commentActions';
import { getTicketHistory } from '../../../reducers/kanban/historyActions';
import { updateTicket } from '../../../reducers/kanban/ticketActions';
import { getAgentsFromApi } from '../../../actions/agentes';
import { useToken } from '../../../hooks/useToken';
import { setSelectedContact, setSavedContactDetails } from '../../../actions/contacts/contacts';
import { messagesSetSelectContactMessage, messagesLoading, getContactMessagesByIdContact } from '../../../actions/messages';
import { client } from '../../../axios/client';

// Iconos
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DescriptionIcon from '@mui/icons-material/Description';
import SubjectIcon from '@mui/icons-material/Subject';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BlockIcon from '@mui/icons-material/Block';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

// Estilos personalizados
const StyledDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        minWidth: 500,
        maxWidth: 900,
        maxHeight: '85vh', // Reducido de 95vh a 85vh
        height: '85vh', // Reducido de 95vh a 85vh
        overflow: 'hidden',
        margin: '20px' // Añadido margen para evitar que toque los bordes de la pantalla
    },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
}));

const ContentSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2), // Reducido de 3 a 2
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    flex: 1, // Para asegurar que ocupe todo el espacio disponible
    maxHeight: 'calc(100% - 10px)' // Asegurar que no desborde
}));

const StyledTabs = styled(Tabs)(() => ({
    '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0'
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.95rem',
        minHeight: 48,
        transition: 'all 0.2s'
    }
}));


const InfoBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '1px solid rgba(0,0,0,0.05)',
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(2.5),
    transition: 'all 0.2s'
}));

/**
 * Componente para mostrar la información de contacto
 */
const ContactInfo = ({ contactName, contactPhone, contactEmail }) => {
    return (
        <InfoBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                    <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="500">
                    Información de Contacto
                </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#f0f0f0', color: '#757575' }}>
                        <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Nombre
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                            {contactName || 'Contacto no especificado'}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#f0f0f0', color: '#757575' }}>
                        <PhoneIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Teléfono
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                            {contactPhone}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </InfoBox>
    );
};

/**
 * Componente para mostrar la información del agente asignado
 */
const AgentInfo = ({ agentName, agentRole, assignedTo, ticketId, onUpdateAgent }) => {
    // Determinar si hay un agente asignado basado en el ID del agente
    const hasAssignedAgent = !!assignedTo;

    // Estado para controlar el modal de asignación de agente
    const [assignAgentModalOpen, setAssignAgentModalOpen] = useState(false);

    // Función para abrir el modal de asignación de agente
    const handleOpenAssignModal = () => {
        setAssignAgentModalOpen(true);
    };

    // Función para cerrar el modal de asignación de agente
    const handleCloseAssignModal = () => {
        setAssignAgentModalOpen(false);
    };

    return (
        <InfoBox>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                        <SupportAgentIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="500">
                        Agente Asignado
                    </Typography>
                </Box>
                <Tooltip title="Asignar agente">
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleOpenAssignModal}
                            sx={{ color: 'text.secondary' }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                <Avatar
                    sx={{
                        bgcolor: '#f0f0f0',
                        width: 36,
                        height: 36,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#757575'
                    }}
                >
                    {hasAssignedAgent && agentName && agentName.length > 0 ? (
                        agentName.charAt(0).toUpperCase()
                    ) : (
                        '—'
                    )}
                </Avatar>
                <Box>
                    <Typography variant="body1" fontWeight="500">
                        {hasAssignedAgent ? agentName : 'Sin asignar'}
                    </Typography>
                </Box>
            </Box>

            {/* Modal para asignar agente */}
            <AssignAgentModal
                open={assignAgentModalOpen}
                onClose={handleCloseAssignModal}
                ticketId={ticketId}
                currentAgentId={assignedTo}
                onUpdateAgent={onUpdateAgent}
            />
        </InfoBox>
    );
};

/**
 * Componente para mostrar la descripción del ticket
 */
const TicketDescription = ({ description }) => {
    const normalized = description || 'Sin descripción disponible';
    // Límite de 255 caracteres consistente con el campo de edición del ticket
    const displayed = normalized.length > 255
        ? `${normalized.slice(0, 255)}...`
        : normalized;

    return (
        <InfoBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                    <DescriptionIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="500">
                    Descripción
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', px: 1 }}>
                {displayed}
            </Typography>
        </InfoBox>
    );
};

/**
 * Componente para mostrar información adicional del ticket
 * Muestra los campos personalizados agregados al ticket en formato clave-valor
 */
const AdditionalInfo = ({ metadata }) => {
    const filteredEntries = Object.entries(metadata)
        .filter(([key]) => !['description', 'prioridad'].includes(key));

    return (
        <InfoBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                    <SubjectIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="500">
                    Información adicional
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {filteredEntries.length > 0 ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: 1
                }}>
                    {filteredEntries.map(([key, value]) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                p: 1.5,
                                borderRadius: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Box sx={{ flex: '0 0 40%' }}>
                                <Typography variant="subtitle2" fontWeight="500" color="text.primary">
                                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '0 0 60%' }}>
                                <Typography variant="body2">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No hay información adicional para este ticket.
                    </Typography>
                </Box>
            )}
        </InfoBox>
    );
};

/**
 * Componente para mostrar el historial del ticket
 */
const TicketHistory = ({ timelineItems, loading }) => {
    if (loading) {
        return (
            <InfoBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                        <HistoryIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="500">
                        Historial del Ticket
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            </InfoBox>
        );
    }

    if (!timelineItems || timelineItems.length === 0) {
        return (
            <InfoBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                        <HistoryIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="500">
                        Historial del Ticket
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 6,
                    textAlign: 'center'
                }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Sin historial disponible
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        Este ticket no tiene movimientos registrados
                    </Typography>
                </Box>
            </InfoBox>
        );
    }

    return (
        <InfoBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#f0f0f0', color: '#757575' }}>
                    <HistoryIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="500">
                    Historial del Ticket
                </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            
            <Box sx={{ px: 1 }}>
                {timelineItems.map((item, index) => {
                    const isComment = item.type === 'comment';
                    const isCloseEvent = item.data.event_type === 'ticket_closed';
                    const isUpdateEvent = item.data.event_type === 'ticket_updated';
                    const isReopenEvent = item.data.event_type === 'ticket_reopened';

                    let IconComponent = HistoryIcon;
                    let iconColor = 'primary.main';
                    let eventText = '';

                    if (isComment) {
                        IconComponent = ChatBubbleOutlineIcon;
                        iconColor = 'secondary.main';
                    } else if (isUpdateEvent) {
                        IconComponent = EditNoteIcon;
                        iconColor = 'info.main';
                        eventText = 'Ticket actualizado';
                    } else if (isCloseEvent) {
                        IconComponent = BlockIcon;
                        iconColor = 'error.main';
                        eventText = 'Ticket cerrado';
                    } else if (isReopenEvent) {
                        IconComponent = SettingsBackupRestoreIcon;
                        iconColor = 'success.main';
                        eventText = 'Ticket reabierto';
                    } else if (item.data.old_column_name) {
                        eventText = `Movido de "${item.data.old_column_name || 'N/A'}" a "${item.data.new_column_name || 'N/A'}"`;
                    } else if (item.data.new_column_id) {
                        eventText = `Creado en "${item.data.new_column_name || 'N/A'}"`;
                    } else {
                        eventText = 'Evento de ticket desconocido';
                    }

                    return (
                        <Box 
                            key={item.id}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                mb: 3,
                                position: 'relative'
                            }}
                        >
                            {/* Timeline vertical line */}
                            {index < timelineItems.length - 1 && (
                                <Box 
                                    sx={{
                                        position: 'absolute',
                                        left: 14,
                                        top: 32,
                                        height: 'calc(100% + 12px)',
                                        width: 2,
                                        bgcolor: 'divider'
                                    }}
                                />
                            )}
                            
                            {/* Timeline dot */}
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: iconColor,
                                    mr: 2,
                                    mt: 0.5,
                                    zIndex: 1
                                }}
                            >
                                <IconComponent sx={{ fontSize: 16 }} />
                            </Avatar>
                            
                            {/* Content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {isComment ? (
                                    <>
                                        <Typography variant="body1" fontWeight="500" gutterBottom>
                                            Comentario añadido
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            fontStyle: 'italic', 
                                            color: 'text.primary', 
                                            bgcolor: 'action.hover',
                                            p: 1.5,
                                            borderRadius: 1,
                                            mb: 1
                                        }}>
                                            "{item.data.text}"
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body1" fontWeight="500" gutterBottom>
                                        {eventText}
                                    </Typography>
                                )}
                                
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Por: {item.data.author || item.data.agent_name || 'Agente desconocido'}
                                </Typography>
                                
                                <Typography variant="caption" color="text.disabled">
                                    {item.date 
                                        ? new Date(item.date).toLocaleString('es-ES', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })
                                        : 'Fecha no disponible'
                                    }
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </InfoBox>
    );
};

/**
 * Componente para asignar un agente a un ticket
 */
const AssignAgentModal = ({ open, onClose, ticketId, currentAgentId, onUpdateAgent }) => {
    const dispatch = useDispatch();
    const { getTokenSilent } = useToken();
    const [loading, setLoading] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState(currentAgentId || '');
    const [loadingAgents, setLoadingAgents] = useState(false);

    // Obtener el agente actual y la lista de agentes del estado global
    const currentAgent = useSelector(state => state.agent);
    const { agents } = useSelector(state => state.agentes);

    // Función para cargar la lista de agentes
    const loadAgents = useCallback(async () => {
        try {
            setLoadingAgents(true);
            const token = await getTokenSilent();

            if (!token) {
                console.error('No se pudo obtener token para agentes');
                return;
            }

            // Obtener el ID del cliente del agente actual
            const id_customer = currentAgent?.id_customer;
            if (!id_customer) {
                console.error('No se pudo obtener el ID del cliente');
                return;
            }

            // Usar la acción de Redux para cargar los agentes
            await dispatch(getAgentsFromApi(id_customer, token));

            // Establecer el agente seleccionado actualmente
            if (currentAgentId) {
                setSelectedAgentId(currentAgentId);
            }
        } catch (error) {
            console.error('Error al cargar la lista de agentes:', error);
        } finally {
            setLoadingAgents(false);
        }
    }, [getTokenSilent, currentAgent?.id_customer, dispatch, currentAgentId]);

    // Cargar la lista de agentes al abrir el modal
    useEffect(() => {
        if (open) {
            // Siempre que se abre el modal, se establece el agente actual del ticket
            setSelectedAgentId(currentAgentId || '');

            // Si la lista de agentes no está cargada, se carga
            if (currentAgent?.id_customer && (!agents || !agents.items || agents.items.length === 0)) {
                loadAgents();
            }
        }
    }, [open, currentAgentId, currentAgent, agents, loadAgents]);

    // Función para manejar el cambio de agente seleccionado
    const handleAgentChange = (event) => {
        setSelectedAgentId(event.target.value);
    };

    // Función para guardar el agente asignado
    const handleSave = async () => {
        try {
            setLoading(true);
            
            // Obtener el token de autenticación
            const token = await getTokenSilent();
            if (!token) {
                throw new Error('No se pudo obtener el token de autenticación');
            }

            // Obtener el nombre del agente seleccionado
            const selectedAgent = agents?.items?.find(a => a._id === selectedAgentId);
            const agentName = selectedAgent ? (selectedAgent.email ? selectedAgent.email.split('@')[0] : selectedAgent.display_name) : '';

            // Preparar los datos para actualizar el ticket
            const updateData = {
                assigned_to_agent: selectedAgentId || null,
                assigned_agent_name: agentName || '',
                token
            };

    

            // Llamar a la API para actualizar el ticket
            await dispatch(updateTicket(ticketId, updateData, token));
    

            // Crear un objeto con los datos actualizados para pasar al componente padre
            const updatedTicketData = {
                id: ticketId,
                assigned_to_agent: selectedAgentId || null,
                assigned_agent_name: agentName || ''
            };

            // Notificar al componente padre
            if (onUpdateAgent) {
                onUpdateAgent(selectedAgentId, agentName);
            }

            // Esperar un momento para que Redux actualice el estado
            setTimeout(() => {
                // Cerrar el modal con los datos actualizados
                onClose(updatedTicketData);
            }, 100);
        } catch (error) {
            console.error('Error al asignar agente:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Asignar Agente</Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} aria-label="cerrar">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loadingAgents ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Cargando agentes...
                        </Typography>
                    </Box>
                ) : (
                    <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                        <InputLabel id="assign-agent-label">Asignar a</InputLabel>
                        <Select
                            labelId="assign-agent-label"
                            value={selectedAgentId}
                            onChange={handleAgentChange}
                            label="Asignar a"
                            disabled={loading}
                        >
                            <MenuItem value="">
                                <em>Sin asignar</em>
                            </MenuItem>
                            {agents && agents.items && agents.items.map(agent => (
                                <MenuItem key={agent._id} value={agent._id}>
                                    {agent.email ? agent.email.split('@')[0] : agent.display_name || 'Agente'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};


/**
 * Componente que muestra un modal con los detalles completos de un ticket
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.open - Controla si el modal está visible
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Function} props.onEditSuccess - Función a llamar cuando un ticket se edita exitosamente
 * @param {Object} props.ticket - Datos del ticket a mostrar
 * @param {Object} props.column - Columna donde se encuentra el ticket
 * @returns {JSX.Element} Componente del modal de detalles
 */
const TicketDetailModal = ({ open, onClose, onEditSuccess, ticket, column, onReopenSuccess, onUpdate }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getTokenSilent } = useToken();
    const [activeTab, setActiveTab] = useState('conversation');
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isReopening, setIsReopening] = useState(false);


    // Adaptación a la nueva estructura del estado después de la refactorización
    const { comments: { comments = {}, loading: loadingComments = false } = { comments: {}, loading: false } } = useSelector(state => state.kanban);
    const { history: { history = {}, loading: loadingHistory = false } = { history: {}, loading: false } } = useSelector(state => state.kanban);
    const contactsMessages = useSelector(state => state.messages.contactsMessages);
    const agent = useSelector((state) => state.agent);

    // Estado para almacenar la información del contacto
    const [contactData, setContactData] = useState(null);
    const [loadingContact, setLoadingContact] = useState(false);

    // Obtener información del usuario logueado desde MSAL
    const { accounts } = useMsal();
    const userProfile = accounts[0] || {};

    const handleEditSuccess = (updatedTicket) => {
        // 1. Cerrar el modal de edición
        setIsEditModalOpen(false);
        // 2. Mostrar el modal de "Guardando"
        setIsSaving(true);

        if (onUpdate) {
            onUpdate();
        }

        // 3. Cerrar el modal de detalles y el de guardando después de un momento
        setTimeout(() => {
            setIsSaving(false); // hide custom loading modal
            onClose(updatedTicket); // close detail modal
        }, 2000);
    };

    // Cualquier agente autenticado puede editar (fuente confiable: MSAL)
    const canEdit = !!agent || !!accounts[0];

    // Referencias para evitar múltiples peticiones
    const ticketIdRef = useRef(null);
    const contactIdRef = useRef(null);
    const fetchingContactRef = useRef(false);
    const isMountedRef = useRef(true);

    // useEffect para manejar el montaje/desmontaje
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Obtener comentarios del ticket actual
    const ticketComments = useMemo(() => (ticket && comments[ticket.id]) || [], [ticket, comments]);

    // Obtener historial del ticket actual
    const ticketHistory = useMemo(() => (ticket && history[ticket.id]) || [], [ticket, history]);

    // Usar useMemo para combinar historial y comentarios en una sola línea de tiempo
    const timelineItems = useMemo(() => {
        if (!ticket) return []; // Retornar array vacío si no hay ticket

        const historyItems = (ticketHistory || []).map(item => ({
            type: 'history',
            id: `hist-${item.id}`,
            date: new Date(item.changed_at),
            data: item
        }));

        const commentItems = (ticketComments || []).map(item => ({
            type: 'comment',
            id: `comm-${item.id}`,
            date: new Date(item.date),
            data: item
        }));

        const combined = [...historyItems, ...commentItems];
        
        // Ordenar por fecha, del más reciente al más antiguo
        combined.sort((a, b) => b.date - a.date);

        return combined;
    }, [ticket, ticketHistory, ticketComments]);

    // useEffect para obtener comentarios
    useEffect(() => {
        // Solo ejecutar si el modal está abierto y hay un ticket válido
        if (open && ticket?.id) {
            // Verificar si el ID del ticket ha cambiado
            if (ticketIdRef.current !== ticket.id) {
                ticketIdRef.current = ticket.id;

                const fetchComments = async () => {
                    try {
                        const token = await getTokenSilent();
                        dispatch(getTicketComments(ticket.id, token));
                    } catch (error) {
                        console.error('Error al obtener comentarios:', error);
                    }
                };

                fetchComments();
            }
        }
    }, [open, ticket, dispatch, getTokenSilent]);

    // useEffect para obtener historial y comentarios cuando se active la pestaña de historial
    useEffect(() => {
        if (open && ticket?.id && activeTab === 'history') {
            const fetchData = async () => {
                try {
                    const token = await getTokenSilent();
                    // Obtener historial si no está cargando y no existe
                    if (!loadingHistory && !history[ticket.id]) {
                        dispatch(getTicketHistory(ticket.id, token));
                    }
                    // Obtener comentarios si no están cargando y no existen
                    if (!loadingComments && !comments[ticket.id]) {
                        dispatch(getTicketComments(ticket.id, token));
                    }
                } catch (error) {
                    console.error('Error al obtener datos para el historial:', error);
                }
            };
            fetchData();
        }
    }, [open, ticket?.id, activeTab, dispatch, getTokenSilent, history, comments, loadingHistory, loadingComments]);

    // useEffect para obtener información del contacto (OPTIMIZADO)
    useEffect(() => {
        if (open && ticket?.id_contact && !fetchingContactRef.current) {
            // Verificar si ya tenemos datos para este contacto específico
            if (contactIdRef.current === ticket.id_contact && contactData) {
                return; // Ya tenemos los datos de este contacto
            }

            // Marcar que estamos obteniendo datos para este contacto
            fetchingContactRef.current = true;
            contactIdRef.current = ticket.id_contact;

            const fetchContactData = async () => {
                setLoadingContact(true);
                try {
                    const token = await getTokenSilent();
                    const response = await client.get(`/contacts/id/${ticket.id_contact}`, {
                        headers: {'Authorization': `Bearer ${token}`}
                    });
                    if (isMountedRef.current) {
                        setContactData(response.data);
                    }
                } catch (error) {
                    console.error('Error al obtener datos del contacto:', error);
                    // En caso de error, usar datos limitados del ticket
                    const fallbackContactData = {
                        _id: ticket.id_contact,
                        name: ticket.name_contact || '',
                        phone: null, // Marcamos como null para indicar que no se pudo obtener
                        email: null
                    };
                    if (isMountedRef.current) {
                        setContactData(fallbackContactData);
                    }
                } finally {
                    if (isMountedRef.current) {
                        setLoadingContact(false);
                    }
                    fetchingContactRef.current = false; // Permitir futuras peticiones
                }
            };

            fetchContactData();
        }
    }, [open, ticket?.id_contact, getTokenSilent, contactData, ticket?.name_contact]);

    // Limpiar datos del contacto cuando se cierre el modal
    useEffect(() => {
        if (!open) {
            if (isMountedRef.current) {
                setContactData(null);
            }
            contactIdRef.current = null;
            fetchingContactRef.current = false;
        }
    }, [open]);

    if (!ticket) return null;

    // Manejar cambio de pestaña
    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
    };

    const handleConfirmCloseTicket = async () => {
        if (!ticket || !ticket.id) return;
        setIsClosing(true);
        try {
            const token = await getTokenSilent();
            if (!token) {
                throw new Error("Token de autenticación no disponible.");
            }
            
            // Usar userProfile de MSAL que sí tiene los datos correctos del usuario autenticado
            const agentId = userProfile?.localAccountId || agent?._id || agent?.id;
            const agentName = userProfile?.username 
                ? userProfile.username.split('@')[0]  // "jose.rodriguez@yupio.mx" -> "jose.rodriguez"
                : (agent?.email ? agent.email.split('@')[0] : agent?.display_name) || 'Agente';
            
            const updateData = {
                is_active: false,
                changed_by_agent_id: agentId,
                changed_by_agent_name: agentName
            };
    
            await dispatch(updateTicket(ticket.id, updateData, token));
            
            if (onUpdate) {
                onUpdate();
            }
            
            if (isMountedRef.current) {
                setConfirmCloseOpen(false);
            }
            onClose(ticket.id);

        } catch (error) {
            console.error('Error al cerrar ticket:', error);
        } finally {
            if (isMountedRef.current) {
                setIsClosing(false);
            }
        }
    };

    const handleReopenTicket = async () => {
        if (!ticket || !ticket.id) return;
        setIsReopening(true);
        try {
            const token = await getTokenSilent();
            if (!token) {
                throw new Error("Token de autenticación no disponible.");
            }
            
            const agentId = userProfile?.localAccountId || agent?._id || agent?.id;
            const agentName = userProfile?.username 
                ? userProfile.username.split('@')[0]
                : (agent?.email ? agent.email.split('@')[0] : agent?.display_name) || 'Agente';
            
            const updateData = {
                is_active: true,
                changed_by_agent_id: agentId,
                changed_by_agent_name: agentName
            };
    
            await dispatch(updateTicket(ticket.id, updateData, token));
            
            if (onUpdate) {
                onUpdate();
            }

            if (onReopenSuccess) {
                onReopenSuccess(ticket.id);
            } else {
                onClose(); // Fallback por si no se pasa la función
            }

        } catch (error) {
            console.error('Error al reabrir ticket:', error);
        } finally {
            if (isMountedRef.current) {
                setIsReopening(false);
            }
        }
    };

    // Manejar agregar comentario
    const handleAddComment = async (text) => {
        try {
            const token = await getTokenSilent();
            const columnName = column?.name || 'Sin columna';
            const userName = userProfile?.name || '';

            const params = {
                ticketId: ticket.id,
                text,
                columnName,
                token,
                userName
            };

            dispatch(addTicketCommentWithUserName(params));
        } catch (error) {
            console.error('Error al agregar comentario:', error);
        }
    };

    // Función para ver el perfil del contacto
    const handleViewProfile = async () => {
        if (ticket.id_contact) {
            try {
                const token = await getTokenSilent();

                const response = await client.get(`/contacts/id/${ticket.id_contact}`, {
                    headers: {'Authorization': `Bearer ${token}`}
                });

                const contactData = response.data;

                dispatch(setSelectedContact(contactData));
                dispatch(setSavedContactDetails(contactData));

                navigate(`/admin/contacts/${ticket.id_contact}`);
            } catch (error) {
                console.error('Error al navegar al perfil del contacto:', error);

                const fallbackContactData = {
                    _id: ticket.id_contact,
                    name: ticket.contact_name || '',
                    phone: null,
                    email: null
                };

                dispatch(setSelectedContact(fallbackContactData));
                dispatch(setSavedContactDetails(fallbackContactData));

                navigate(`/admin/contacts/${ticket.id_contact}`);
            }
        } else {
            console.warn('No hay ID de contacto disponible para este ticket');
        }
    };

    // Función para ver el chat del contacto
    const handleViewChat = async () => {
        const contactId = ticket.id_contact || ticket.contact?._id;

        if (contactId) {
            try {
                const token = await getTokenSilent();

                const response = await client.get(`/contacts/id/${contactId}`, {
                    headers: {'Authorization': `Bearer ${token}`}
                });

                const contactData = response.data;

                const validContactData = {
                    ...contactData,
                    _id: contactId
                };

                dispatch(setSelectedContact(validContactData));
                dispatch(setSavedContactDetails(validContactData));

                const contactMessage = contactsMessages.find(msg => msg.id_contact === contactId);
                if (contactMessage) {
                    dispatch(messagesSetSelectContactMessage(contactMessage));
                } else {
                    dispatch(messagesLoading(true));

                    dispatch(getContactMessagesByIdContact(
                        contactId,
                        token,
                        'whatsapp',
                        validContactData.unread_message || 0
                    ));

                    await new Promise(resolve => setTimeout(resolve, 800));
                }

                navigate('/admin/chat');
            } catch (error) {
                console.error('Error al navegar al chat del contacto:', error);

                const fallbackContactData = {
                    _id: contactId,
                    name: ticket.contact_name || ticket.name_contact || '',
                    phone: null,
                    email: null,
                    unread_message: 0
                };

                dispatch(setSelectedContact(fallbackContactData));
                dispatch(setSavedContactDetails(fallbackContactData));

                try {
                    const token = await getTokenSilent();
                    dispatch(messagesLoading(true));

                    dispatch(getContactMessagesByIdContact(
                        contactId,
                        token,
                        'whatsapp',
                        0
                    ));

                    await new Promise(resolve => setTimeout(resolve, 800));

                    navigate('/admin/chat');
                } catch (chatError) {
                    console.error('Error al cargar mensajes del contacto:', chatError);
                    navigate('/admin/chat');
                }
            }
        } else {
            console.warn('No hay ID de contacto disponible para este ticket');
            alert('No se puede abrir el chat porque no hay información de contacto disponible.');
        }
    };

    // Extraer metadatos del ticket si están en formato JSON
    const extractMetadata = (ticket) => {
        if (!ticket.meta_data) return {};

        try {
            return typeof ticket.meta_data === 'string'
                ? JSON.parse(ticket.meta_data)
                : ticket.meta_data;
        } catch {
            return {};
        }
    };

    // Obtener información del ticket
    const metadata = extractMetadata(ticket);
    const ticketDescription = metadata.description || ticket.description || ticket.motivo || '';
    const ticketPriority = metadata.prioridad || ticket.prioridad || 'media';

    // Información del contacto - Usando datos obtenidos automáticamente
    const hasContactName = contactData?.first_name || 
                          contactData?.name ||
                          ticket.name_contact ||
                          ticket.contact_name ||
                          (ticket.contact && ticket.contact.name);
    
    // Función para detectar si un string es un número de teléfono
    const isPhoneNumber = (str) => {
        if (!str || typeof str !== 'string') return false;
        // Detectar si es solo dígitos (posiblemente con + al inicio)
        return /^[+]?[\d\s\-()]+$/.test(str.trim()) && str.trim().length >= 8;
    };
    
    const contactPhone = loadingContact
                        ? 'Cargando...'
                        : contactData?.phone
                        ? String(contactData.phone)
                        : ticket.contact_phone ||
                          (ticket.contact && ticket.contact.phone) ||
                          'No disponible';

    let contactName = hasContactName;

    // Si no hay nombre real, verificar si tenemos teléfono
    if (!contactName) {
        if (contactPhone && contactPhone !== 'No disponible' && contactPhone !== 'Cargando...') {
            contactName = 'Sin asignar nombre';
        } else {
            contactName = 'Contacto no especificado';
        }
    } else {
        // Si el "nombre" es realmente un número de teléfono, cambiarlo a "Sin asignar nombre"
        if (isPhoneNumber(contactName)) {
            contactName = 'Sin asignar nombre';
        }
    }
    
    const contactEmail = loadingContact 
                        ? 'Cargando...' 
                        : contactData?.email ||
                          ticket.contact_email ||
                          (ticket.contact && ticket.contact.email) ||
                          'No disponible';
    
    // Información del agente - Adaptado para manejar IDs como string y mostrar nombre completo
    const agentName = ticket.assigned_agent_name ||
                     ticket.name_agent ||
                     ticket.assigned_name ||
                     (ticket.agent && ticket.agent.name) ||
                     (ticket.agentData && ticket.agentData.name) ||
                     (ticket.assigned_to_agent ? `Agente #${ticket.assigned_to_agent}` : 'Sin asignar');
    const agentRole = ticket.assigned_role ||
                     (ticket.agent && ticket.agent.role) ||
                     '';
    const assignedTo = ticket.assigned_to_agent ||
                      ticket.assigned_to ||
                      ticket.id_agent ||
                      (ticket.agent && ticket.agent._id);

    // Obtener color por prioridad
    const getPriorityColor = () => {
        switch (ticketPriority) {
            case 'baja': return 'success';
            case 'media': return 'warning';
            case 'alta': return 'error';
            default: return 'grey';
        }
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            sx={{ '& .MuiDialog-container': { alignItems: 'center' } }} // Centrar verticalmente
        >
            <HeaderSection>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Avatar
                            sx={{
                                width: 42,
                                height: 42,
                                bgcolor: 'primary.main',
                                fontWeight: 'bold',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            {contactName && contactName.length > 0 ? contactName[0].toUpperCase() : 'C'}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" component="h2" fontWeight="500">
                                {contactName || contactPhone}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                    color={getPriorityColor()}
                                    label={ticketPriority.charAt(0).toUpperCase() + ticketPriority.slice(1)}
                                    size="small"
                                    sx={{
                                        borderRadius: 1,
                                        fontWeight: 500,
                                        height: 22,
                                        fontSize: '0.75rem',
                                        '& .MuiChip-label': {
                                            paddingLeft: 8,
                                            paddingRight: 8
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="text"
                            startIcon={<AccountCircleIcon />}
                            size="small"
                            color="primary"
                            sx={{ borderRadius: 1, textTransform: 'none' }}
                            onClick={handleViewProfile}
                        >
                            Ver perfil
                        </Button>
                        <Button
                            variant="text"
                            startIcon={<ChatIcon />}
                            size="small"
                            color="primary"
                            sx={{ borderRadius: 1, textTransform: 'none' }}
                            onClick={handleViewChat}
                        >
                            Ver conversación
                        </Button>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={onClose}
                            aria-label="cerrar"
                            sx={{ ml: 0.5 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </HeaderSection>

            <StyledTabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
                <Tab
                    value="conversation"
                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon fontSize="small" />
                        <span>Conversación</span>
                    </Box>}
                />
                <Tab
                    value="details"
                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" />
                        <span>Detalles</span>
                    </Box>}
                />
                <Tab
                    value="history"
                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon fontSize="small" />
                        <span>Historial</span>
                    </Box>}
                />
            </StyledTabs>

            <DialogContent dividers sx={{ height: 'calc(85vh - 120px)', p: 0 }}>
                <ContentSection>
                    {activeTab === 'conversation' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
                            <TicketComments
                                comments={ticketComments}
                                onAddComment={handleAddComment}
                                loading={loadingComments}
                                columnName={column?.name}
                            />
                        </Box>
                    ) : activeTab === 'history' ? (
                        <TicketHistory 
                            timelineItems={timelineItems}
                            loading={loadingHistory || loadingComments}
                        />
                    ) : (
                        <>
                            {/* Grid con detalles del ticket */}
                            <Grid container spacing={3}>
                                {/* Columna izquierda */}
                                <Grid item xs={12} md={6}>
                                    <TicketDescription description={ticketDescription} />
                                    <AdditionalInfo metadata={metadata} />
                                </Grid>

                                {/* Columna derecha con información de contacto y asignación */}
                                <Grid item xs={12} md={6}>
                                    <ContactInfo
                                        contactName={contactName}
                                        contactPhone={contactPhone}
                                        contactEmail={contactEmail}
                                    />
                                    <AgentInfo
                                        agentName={agentName}
                                        agentRole={agentRole}
                                        assignedTo={assignedTo}
                                        ticketId={ticket.id}
                                        onUpdateAgent={(agentId, agentName) => {
                                            // No cerramos el modal principal, solo actualizamos los datos
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </ContentSection>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)', bgcolor: 'background.paper', display: 'flex', gap: 2 }}>
                {canEdit && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<EditNoteIcon />}
                        onClick={() => setIsEditModalOpen(true)}
                        disabled={isClosing || isReopening}
                        fullWidth
                        sx={{ py: 1.25, fontSize: '0.9rem', fontWeight: 'bold' }}
                    >
                        Editar Ticket
                    </Button>
                )}
                {ticket.is_active ? (
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={() => setConfirmCloseOpen(true)}
                        disabled={!ticket.is_active || isClosing}
                        fullWidth
                        sx={{ py: 1.25, fontSize: '0.9rem', fontWeight: 'bold' }}
                    >
                        Cerrar Ticket
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<SettingsBackupRestoreIcon />}
                        onClick={handleReopenTicket}
                        disabled={isReopening}
                        fullWidth
                        sx={{ 
                            py: 1.25, 
                            fontSize: '0.9rem', 
                            fontWeight: 'bold',
                            backgroundColor: '#2e7d32',
                            '&:hover': {
                                backgroundColor: '#1b5e20'
                            }
                        }}
                    >
                        {isReopening ? 'Reabriendo...' : 'Reabrir Ticket'}
                    </Button>
                )}
            </DialogActions>

            {/* Confirmation Dialog for Closing Ticket */}
            <Dialog
                open={confirmCloseOpen}
                onClose={() => setConfirmCloseOpen(false)}
                aria-labelledby="confirm-close-dialog-title"
            >
                <DialogTitle id="confirm-close-dialog-title">Confirmar Cierre de Ticket</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea cerrar este ticket? El ticket desaparecerá del tablero principal y se moverá a la sección de tickets cerrados.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCloseOpen(false)} color="primary" disabled={isClosing}>
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmCloseTicket} color="error" variant="contained" disabled={isClosing}>
                        {isClosing ? <CircularProgress size={24} color="inherit" /> : 'Sí, Cerrar Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para editar ticket */}
            {ticket && (
                <CreateTicketModal
                    open={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    isEditMode={true}
                    ticketToEdit={ticket}
                    boardId={ticket.board_id}
                    onSuccess={handleEditSuccess}
                />
            )}
            
            {/* Notificación de guardado */}
            <Dialog
                open={isSaving}
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        padding: '16px'
                    },
                }}
            >
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Guardando cambios
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Por favor espera mientras procesamos el cambio.
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Notificación de reapertura */}
            <Dialog
                open={isReopening}
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        padding: '16px'
                    },
                }}
            >
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Reabriendo ticket
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Por favor espera, el ticket volverá al tablero.
                    </Typography>
                </DialogContent>
            </Dialog>
        </StyledDialog>
    );
};

export default TicketDetailModal;